import {
  type PDFLabelSetConstructor,
  type OrderItemLabels,
  type POLabels,
} from '../../entities/PDFLabelSet'
import {
  type OrderItemLabelsApiModel,
  type PDFLabelSetApiModel,
  type POLabelsApiModel,
} from '../api-models/PDFLabelSetApiModel'

/**
 * Domain (camelCase) key → raw kvstore API key for the `value.po` map.
 * Inverse of the key tracking in `mapPDFLabelSetApiModel`. Unknown
 * (custom-field-label) keys pass through unchanged in both directions.
 */
export const PO_LABEL_API_KEY_BY_DOMAIN_KEY: Record<string, string> = {
  poNumber: 'PO_Number',
  purchaseOrder: 'Purchase Order',
  shipTo: 'Ship To',
  comment: 'comment',
  creditcard: 'creditcard',
  date: 'date',
  discount: 'discount',
  freight: 'freight',
  other: 'other',
  paymentTerms: 'payment_terms',
  prefVendor: 'pref_vendor',
  promiseDate: 'promise_date',
  shippingMethod: 'shipping_method',
  shippingTerms: 'shipping_terms',
  subtotal: 'subtotal',
  tax: 'tax',
  total: 'total',
}

/** Domain (camelCase) key → raw kvstore API key for the `value.orderitem` map. */
export const ORDER_ITEM_LABEL_API_KEY_BY_DOMAIN_KEY: Record<string, string> = {
  itemDescription: 'Item Description',
  totalPrice: 'Total Price',
  poNote: 'po_note',
  price: 'price',
  quantity: 'quantity',
  sku: 'sku',
  unit: 'unit',
}

const mapLabelsToApiKeys = (
  labels: POLabels | OrderItemLabels,
  apiKeyByDomainKey: Record<string, string>
): Record<string, string> => {
  return Object.keys(labels).reduce<Record<string, string>>((acc, key) => {
    const value = labels[key]

    if (value !== undefined) {
      acc[apiKeyByDomainKey[key] ?? key] = value
    }

    return acc
  }, {})
}

export type PDFLabelSetWritePayload = Pick<PDFLabelSetApiModel, 'key' | 'value'>

export const mapPDFLabelSetConstructorToApiModel = (
  data: PDFLabelSetConstructor
): PDFLabelSetWritePayload => ({
  key: data.key,
  value: {
    name: data.value.name,
    po: mapLabelsToApiKeys(
      data.value.po,
      PO_LABEL_API_KEY_BY_DOMAIN_KEY
    ) as POLabelsApiModel,
    orderitem: mapLabelsToApiKeys(
      data.value.orderitem,
      ORDER_ITEM_LABEL_API_KEY_BY_DOMAIN_KEY
    ) as OrderItemLabelsApiModel,
  },
})
